import { Button, Result, Space, Spin, Table, Tag } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import API from 'helpers/API';
import { HeadingH1 } from 'components/ui';
import { getReclaimStatus } from 'utils/CodeMapper';
import { getApiErrorMessage } from 'utils/getApiErrorMessage';

const Page = () => {
  const navigate = useNavigate();
  const [reclaims, setReclaims] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState();
  const role = useMemo(() => localStorage.getItem('@h2tms/role'), []);

  useEffect(() => {
    let isMounted = true;

    const fetchReclaims = async () => {
      setIsLoading(true);
      setFetchError(undefined);

      const response = await API.get('/customer/reclaims');

      if (!isMounted) return;

      if (response?.success) {
        setReclaims(response.data?.reclaims ?? []);
      } else {
        setReclaims([]);
        setFetchError(getApiErrorMessage(response, '회수이력을 불러오지 못했습니다.'));
      }

      setIsLoading(false);
    };

    fetchReclaims();

    return () => {
      isMounted = false;
    };
  }, []);

  const columns = [
    {
      title: '상태',
      dataIndex: 'status',
      width: 110,
      align: 'center',
      render: (value) => <Tag>{getReclaimStatus(value)}</Tag>,
    },
    {
      title: '회수 요청일',
      dataIndex: 'reclaimDate',
      width: 120,
      align: 'center',
      render: (value) =>
        value ? `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}` : '-',
    },
    {
      title: '수량',
      dataIndex: 'quantity',
      width: 80,
      align: 'center',
    },
    {
      title: '고객명',
      dataIndex: 'customerName',
      width: 120,
    },
    {
      title: '회사명',
      dataIndex: 'customerCompany',
      width: 160,
    },
    {
      title: '전화번호',
      dataIndex: 'customerPhone',
      width: 140,
    },
    {
      title: '주소',
      render: (_, record) =>
        `${record.customerAddress} ${record.customerAddressDetail ?? ''}`.trim(),
    },
    {
      title: '등록일시',
      dataIndex: 'createdAt',
      width: 180,
      render: (value) => dayjs(value).format('YYYY-MM-DD HH:mm:ss'),
    },
  ];

  if (isLoading) {
    return (
      <Space direction="vertical" align="center" size={16} style={{ width: '100%', padding: '80px 0' }}>
        <Spin size="large" />
      </Space>
    );
  }

  if (fetchError) {
    return (
      <Result
        status="error"
        title="회수이력을 불러오지 못했습니다."
        subTitle={fetchError}
      />
    );
  }

  return (
    <>
      <HeadingH1 title={'회수이력'} />
      {role === 'CUSTOMER_MANAGER' && (
        <div style={{ marginBottom: 16 }}>
          <Button type="primary" onClick={() => navigate('/customer/reclaims/new')}>
            회수하기
          </Button>
        </div>
      )}
      <Table
        rowKey={'rid'}
        columns={columns}
        dataSource={reclaims}
        onRow={(record) => ({
          onClick: () => navigate(`/customer/reclaims/${record.rid}`),
        })}
      />
    </>
  );
};

export default Page;
